/**
 * ProfileMenu — User profile dropdown menu.
 * Mirrors Tauri ProfileMenu for feature parity.
 */
import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '@/store/appStore';
import { useAuth } from '@workos-inc/authkit-react';
import {
  User,
  Settings,
  LogOut,
  ChevronDown,
  Moon,
  Sun,
  Keyboard,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileMenuProps {
  className?: string;
}

export function ProfileMenu({ className }: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { signOut } = useAuth();
  const { setActivePanel, setShowWelcomeModal } = useAppStore();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = [
    {
      icon: User,
      label: 'Profile',
      onClick: () => {
        setActivePanel('settings');
        setIsOpen(false);
      },
    },
    {
      icon: Settings,
      label: 'Settings',
      onClick: () => {
        setActivePanel('settings');
        setIsOpen(false);
      },
    },
    {
      icon: Keyboard,
      label: 'Keyboard Shortcuts',
      onClick: () => {
        setShowShortcuts(true);
        setIsOpen(false);
      },
    },
    {
      icon: HelpCircle,
      label: 'Help & Welcome',
      onClick: () => {
        setShowWelcomeModal(true);
        setIsOpen(false);
      },
    },
    null, // Separator
    {
      icon: LogOut,
      label: 'Sign out',
      onClick: () => void signOut(),
      variant: 'danger' as const,
    },
  ];

  return (
    <>
      <div ref={menuRef} className={cn("relative", className)}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
            isOpen
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          )}
        >
          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-4 w-4 text-primary" />
          </div>
          <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-card shadow-lg z-50 py-1">
            <div className="px-3 py-2 border-b border-border">
              <p className="font-medium text-sm">My Account</p>
              <p className="text-xs text-muted-foreground">user@example.com</p>
            </div>
            <div className="py-1">
              {menuItems.map((item, index) =>
                item === null ? (
                  <div key={`sep-${index}`} className="my-1 border-t border-border" />
                ) : (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors",
                      item.variant === 'danger'
                        ? "text-destructive hover:bg-destructive/10"
                        : "text-foreground hover:bg-muted"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <KeyboardShortcutsModal onClose={() => setShowShortcuts(false)} />
      )}
    </>
  );
}

function KeyboardShortcutsModal({ onClose }: { onClose: () => void }) {
  const shortcuts = [
    { key: 'Ctrl + Z', description: 'Undo' },
    { key: 'Ctrl + Shift + Z', description: 'Redo' },
    { key: 'Delete / Backspace', description: 'Delete selected layer' },
    { key: 'Space + Drag', description: 'Pan canvas' },
    { key: 'Shift + Resize', description: 'Lock aspect ratio' },
    { key: 'Ctrl + K', description: 'Open search' },
    { key: '@', description: 'Mention entity in chat' },
    { key: 'Arrow Keys', description: 'Nudge selected layer (with Shift for 10px)' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-primary" />
            Keyboard Shortcuts
          </h2>
          <button onClick={onClose} className="rounded p-1.5 text-muted-foreground hover:bg-muted">
            ×
          </button>
        </div>
        <div className="space-y-2">
          {shortcuts.map((shortcut, index) => (
            <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-sm text-muted-foreground">{shortcut.description}</span>
              <kbd className="px-2 py-1 rounded bg-muted text-xs font-mono">{shortcut.key}</kbd>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
